<template>
	<div>
		<div class="text-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<b-card v-else-if="detectors?.length > 0" no-body>
			<b-list-group flush>
				<b-list-group-item v-for="item in detectors" :key="item.id" class="d-flex" style="gap: .5em" :class="item.status == 'disabled' && 'text-muted'">
					<a href="#" @click.prevent="playTestAlert(item)" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
					<div class="flex-fill">
						{{item.word}}
						<b-badge v-for="flag in Object.keys(item.flags).filter(k => item.flags[k])" :key="flag" variant="secondary" class="mr-1">{{flag.replaceAll('-', ' ')}}</b-badge>
						<div v-if="item.message" class="text-muted small">{{item.message}}</div>
					</div>
					<b-avatar-group size="1.5rem" rounded overlap=".05" v-if="item.media && item.media.length">
						<b-avatar v-if="item.media.filter(f => f.mime && f.mime[0] == 'image').length > 0" :src="item.media.filter(f => f.mime && f.mime[0] == 'image')[0].url" v-b-tooltip.hover.bottom.v-info :title="`Has ${item.media.filter(f => f.mime && f.mime[0] == 'image').length} image${item.media.filter(f => f.mime && f.mime[0] == 'image').length == 1 ? '' : 's'} configured`" />
						<b-avatar v-if="item.media.filter(f => f.mime && f.mime[0] == 'audio').length > 0" v-b-tooltip.hover.bottom.v-info :title="`Has ${item.media.filter(f => f.mime && f.mime[0] == 'audio').length} sound${item.media.filter(f => f.mime && f.mime[0] == 'audio').length == 1 ? '' : 's'} configured`"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></b-avatar>
					</b-avatar-group>
					<a href="#" @click.prevent="edit(item.id)" v-b-tooltip.hover.right title="Edit word detector"><b-icon icon="pencil-fill"></b-icon></a>
				</b-list-group-item>
			</b-list-group>
		</b-card>
		<b-card v-else class="text-center text-muted p-5">No tower detectors.<br />Tower detectors are a fun way to interact with chat. Have chatters spell out a word, one message at a time and trigger a notification when they succeed!<br /></b-card>
		<div class="text-center my-3">
			<b-button @click="edit()" variant="success"><b-icon icon="plus"></b-icon> Add a word</b-button>
		</div>
		<b-modal ref="editModal" title="Edit word detector">
			<b-form @submit.stop.prevent="save(); return false" id="towerEditForm">
				<b-form-row>
					<b-col>
						<b-form-group label="Word">
							<b-form-input v-model="curDetector.word" required pattern="[A-Za-z0-9]+"/>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message to display">
							<b-form-input v-model="curDetector.message"/>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-row>
					<b-col>
						<b-form-group>
							<b-form-checkbox switch v-model="curDetector.flags['cross-platform']">Cross platform</b-form-checkbox>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group>
							<b-form-checkbox switch v-model="curDetector.flags['same-chatter']">Allow same chatter</b-form-checkbox>
						</b-form-group>
					</b-col>
				</b-form-row>
				<p class="text-muted">Users can use case insensitive and special characters (𝔸𝐁ℭ🅳)</p>
				<b-form-group label="Media" description="Sounds, images and video">
					<pipi-pupu-file ref="fileUpload" v-model="curDetector.media" accept="image/*,audio/*,video/*" :multiple="true"></pipi-pupu-file>
				</b-form-group>
			</b-form>
			<template #modal-footer="{cancel}">
				<div class="w-100">
					<b-alert variant="danger" :show="!!saveError">{{saveError}}</b-alert>
					<div class="float-left" v-if="curDetector.id">
						<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
					</div>
					<div class="float-right">
						<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
						<b-button variant="success" type="submit" form="towerEditForm" class="mr-2">
							Save
						</b-button>
						<b-button @click="cancel()">
							Cancel
						</b-button>
					</div>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import PipiPupuFile from '@/components/PipiPupuFile.vue';

export default {
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		detectors: [],
		curDetector: { flags: {} },
	}},
	components: {
		PipiPupuFile,
	},
	async mounted() {
		await this.loadDetectors();
	},
	methods: {
		async loadDetectors() {
			try {
				this.loading++;
				this.error = null;
				this.detectors = await this.$api.get('towers');
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async edit(id) {
			console.log('edit', id);
			try {
				this.saving++;
				this.saveError = null;
				this.$refs.editModal.show();
				if(id) {
					this.curDetector = { flags: {} };
					this.curDetector = await this.$api.get(`tower/${encodeURIComponent(id)}`);
				} else {
					this.curDetector = {
						word: '',
						string: '',
						flags: {
							'cross-platform': false,
							'same-chatter': false,
							'case-sensitive': false,
							'special-characters': false,
						},
						media: [],
					};
				}
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		async removeItem(id) {
			await this.$api.delete(`tower/${encodeURIComponent(id)}`);
			await this.loadDetectors();
		},
		async setItemStatus(id, status) {
			await this.$api.patch(`tower/${encodeURIComponent(id)}/status`, status);
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;
				await this.$refs.fileUpload?.waitForUploads();
				if(this.curDetector.id)
					await this.$api.patch(`tower/${encodeURIComponent(this.curDetector.id)}`, this.curDetector);
				else
					await this.$api.post('tower', this.curDetector);
				this.$refs.editModal.hide();
				await this.loadDetectors();
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		async playTestAlert(item) {
			try {
				await this.$api.post('alerts/test', { type: 'tower', data: { towerId: item.id } });
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not send test alert: '+(e.error||String(e)), { title: 'Error', variant: 'danger', solid: true });
			}
		},
	},
};
</script>
